import React from "react";
import parse from "html-react-parser";
import styled, { keyframes } from "styled-components";
import { T } from "@transifex/react";
import { useAiContent } from "hooks/use-ai-content";
import {
  ContentItemWrapper,
  ContentItemHtml,
  ContentItemVideo,
  ContentItemVideoTitle,
} from "./styles";

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const LoadingContainer = styled.div`
  padding: 16px 0;
`;

const LoadingBar = styled.div<{ $width?: string; $height?: string }>`
  background: linear-gradient(
    90deg,
    #f0f0f0 0px,
    #e0e0e0 40px,
    #f0f0f0 80px
  );
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite linear;
  border-radius: 4px;
  width: ${(props) => props.$width || "100%"};
  height: ${(props) => props.$height || "16px"};
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const LoadingText = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  font-size: 14px;
  margin-top: 12px;
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid #e0e0e0;
  border-top-color: #1976d2;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ErrorContainer = styled.div`
  padding: 16px;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #991b1b;
`;

const ErrorText = styled.p`
  margin: 0 0 8px 0;
  font-size: 14px;
`;

const RetryButton = styled.button`
  background: #1976d2;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #1565c0;
  }
`;

interface AiContentItemProps {
  /** HTML content or AI prompt */
  content?: string;
  /** Video URL */
  videoUrl?: string;
  /** Video title */
  videoTitle?: string;
  /** If true, content is an AI prompt that needs to be sent to the specialist API */
  isAiPrompt?: boolean;
  /** Index for data-testid */
  index: number;
}

/**
 * Renders a content item with support for AI-generated content.
 * 
 * When isAiPrompt is true, the content is treated as an AI prompt
 * and sent to the specialist API to generate HTML content.
 * A loading skeleton is displayed while the content is being generated.
 */
export function AiContentItem({
  content,
  videoUrl,
  videoTitle,
  isAiPrompt = false,
  index,
}: AiContentItemProps) {
  const {
    content: generatedContent,
    isLoading,
    error,
    retry,
  } = useAiContent({
    prompt: content || "",
    isAiPrompt,
  });

  // Render loading skeleton for AI content
  if (isAiPrompt && isLoading) {
    return (
      <ContentItemWrapper data-testid={`InterestCard__ContentItem-${index}`}>
        <LoadingContainer data-testid={`InterestCard__ContentItemLoading-${index}`}>
          <LoadingBar $width="90%" />
          <LoadingBar $width="100%" />
          <LoadingBar $width="75%" />
          <LoadingBar $width="85%" />
          <LoadingText>
            <Spinner />
            <T _str="Generating personalized content..." />
          </LoadingText>
        </LoadingContainer>
      </ContentItemWrapper>
    );
  }

  // Render error state for AI content
  if (isAiPrompt && error) {
    return (
      <ContentItemWrapper data-testid={`InterestCard__ContentItem-${index}`}>
        <ErrorContainer data-testid={`InterestCard__ContentItemError-${index}`}>
          <ErrorText>
            <T _str="Unable to load personalized content." />
          </ErrorText>
          <RetryButton onClick={retry}>
            <T _str="Try again" />
          </RetryButton>
        </ErrorContainer>
      </ContentItemWrapper>
    );
  }

  // Determine which content to display
  const displayContent = isAiPrompt ? generatedContent : content;

  // Don't render if there's no content and no video
  if (!displayContent && !videoUrl) {
    return null;
  }

  return (
    <ContentItemWrapper data-testid={`InterestCard__ContentItem-${index}`}>
      {displayContent && (
        <ContentItemHtml data-testid={`InterestCard__ContentItemHtml-${index}`}>
          {parse(displayContent)}
        </ContentItemHtml>
      )}
      {videoUrl && (
        <ContentItemVideo data-testid={`InterestCard__ContentItemVideo-${index}`}>
          <video
            src={videoUrl}
            controls
            preload="metadata"
            aria-label={videoTitle || "Video"}
          />
          {videoTitle && (
            <ContentItemVideoTitle>{videoTitle}</ContentItemVideoTitle>
          )}
        </ContentItemVideo>
      )}
    </ContentItemWrapper>
  );
}
